
import { apiDelete, apiGet, apiPost } from './http';
import { community } from './envelope';
import { localDayStartISO } from './me';
import type { Badges } from './moderation';
import type { CommunityRef, ListEnvelope, QueryParams } from './envelope';
// `QueuedReply` was the Helper's narrower view of a queued row — three fields off
// the same response this module's `OutboxItem` describes in full. Kept as an alias so
// the panel's call site is unchanged; there is one shape and one `queueReply` now.
export type QueuedReply = OutboxItem;

/**
 * Group F — the outbox queue and the Helper devices that drain it.
 * `docs/api-v1/04-v1-surface.md` §3.1 F; the backend twin is `api/v1/outbox.py`.
 *
 *     GET    /v1/communities/{skool_id}/outbox/items          the queue
 *     POST   /v1/communities/{skool_id}/outbox/items          queue a reply
 *     DELETE /v1/communities/{skool_id}/outbox/items/{id}     cancel one
 *     GET    /v1/devices                                      the paired Helpers
 *
 * WHY THIS FILE EXISTS AT ALL, given the legacy routes work. Because the outbox is
 * the one part of the product that WRITES to Skool, and its client-side vocabulary
 * had drifted into four disagreeing shapes for two ideas — "which item is this a
 * reply to" and "who wrote it".
 *
 * FOUR SHAPE CHANGES, EACH ONE A THING A COMPONENT MUST STOP DOING
 *
 *  1. **`target` is `{kind, id}` + resolved blocks, not six flat keys.** The legacy
 *     row carries `target: {excerpt, author_name, author_member_id, post_title,
 *     post_id, skool_url}` — a fourth flattened author triple, counted in
 *     `01-backend-surface.md` §3.1 among thirteen member shapes. Here the author is
 *     §2.1's `MemberRef` and the thread is §2.2's `PostRef`, so `<Avatar>` and the
 *     member link take the same object they take everywhere else. See
 *     `OutboxTarget` for the field-by-field crossing.
 *  2. **The subject moved off the item and onto the target.** `item.subject_kind` /
 *     `item.subject_id` are gone; they are `item.target.kind` / `item.target.id`,
 *     which are the two keys of `target` that are ALWAYS present because they come
 *     off the outbox row itself. The rest of `target` is null when the replied-to
 *     post or comment has since been deleted — where the legacy route emitted an
 *     all-null `OutboxItemTarget()` object so a client's unconditional dereference
 *     would not throw (`03-frozen-contract.md` F4). Nothing has to invent an object
 *     here: `target` is present under the default field set, always.
 *  3. **The reply write is a POST to this collection.** Doc 04 §4.7: a moderation
 *     reply IS an outbox row, so `POST /admin/moderation/reply` becomes
 *     `POST …/outbox/items` and there is exactly one place that row is created.
 *     `body: ""` no longer means "delete the draft" — that was a destructive write
 *     hidden inside a create, and the deletion path is the DELETE below.
 *  4. **The envelope is the standard one.** `{skool_id, items, total, limit, offset,
 *     counts, helper}` becomes `{items, next_cursor, total}` with `community`,
 *     `counts` and `helper` BESIDE them. `limit`/`offset` are request state and are
 *     not echoed — the caller already knows what it asked for.
 *
 * TIMESTAMPS. `GET /outbox/devices` emitted BARE datetimes (that route was retired
 * in wave 8),
 * which a browser reads as local time and which therefore shifted "last seen" by the
 * viewer's UTC offset. Every instant here is UTC-aware; parse with `parseTs`
 * (`lib/admin.ts`), never `new Date(raw)`.
 *
 * WHAT IS NOT HERE. `DELETE /v1/devices/{device_id}` is mounted and works, and no UI
 * calls it — doc 07 §6.1 keeps the revoke CONTROL as a separate design question, so
 * a wrapper for it would be a wrapper with no page. The lease protocol
 * (`/outbox/lease` · `/outbox/ack` · `/outbox/heartbeat`) is the Helper's and is
 * frozen forever; nothing in the web app has ever called it.
 */

import type { MemberRef } from './members';


// ---------------------------------------------------------------------------
// The wire (mirrors `api/v1/outbox.py`'s response models)
// ---------------------------------------------------------------------------

/** Where a queued reply is in its journey to Skool. The same six values the legacy
 *  `OutboxStatus` declares, in the same order the `counts` block emits them —
 *  `api/v1/outbox.py:_STATUSES` is `db.models`' pending + terminal sets and a test
 *  pins the two together, so a seventh status cannot appear on one side only. */
export type OutboxStatus =
  | 'queued'
  | 'leased'
  | 'sent'
  | 'failed'
  | 'unknown'
  | 'discarded';

/** §2.2's `PostRef` — `{id, title, slug, url}` and nothing else. The same block
 *  `CommentItem.post` carries in `@api/v1/posts`; it is spelled out here
 *  rather than imported because it is declared inline there. */
export interface PostRef {
  id: number;
  title: string | null;
  slug: string | null;
  url: string | null;
}

/**
 * What a queued reply answers.
 *
 * THE CROSSING FROM THE LEGACY `OutboxTarget`, which is the whole of what a
 * component has to change:
 *
 *     target.author_name        →  target.author?.name
 *     target.author_member_id   →  target.author?.member_id
 *     target.post_title         →  target.post?.title
 *     target.post_id            →  target.post?.id      (the timeline `?post=` link)
 *     target.skool_url          →  target.url
 *     target.excerpt            →  target.excerpt       (unchanged)
 *     item.subject_kind/_id     →  target.kind / target.id
 *
 * `kind` and `id` are always present. `author`, `post`, `excerpt` and `url` are null
 * together when the replied-to row has been deleted — the row then renders link-less,
 * which is what the legacy all-null object was standing in for.
 */
export interface OutboxTarget {
  kind: 'post' | 'comment';
  id: number;
  excerpt: string | null;
  author: MemberRef | null;
  post: PostRef | null;
  url: string | null;
}

/**
 * One queued/sent reply.
 *
 * `id`, `status` and `kind` are identity and survive any `?fields=`. Everything else
 * is selectable, and an unselected field is OMITTED rather than null
 * (`response_model_exclude_unset`), which is why they are optional here — under the
 * default selection this file asks for, all of them are present EXCEPT `updated_at`,
 * which no card renders (the row shows `queued_at`/`leased_at`/`sent_at`).
 */
export interface OutboxItem {
  id: number;
  status: OutboxStatus;
  /** The outbox kind, `comment_reply` — not the target's kind. Do not confuse it
   *  with `target.kind`, which is what is being replied TO. */
  kind: string;
  target?: OutboxTarget;
  /** The reply text we will post. This is the legacy `draft` under a truer name:
   *  there is no draft state, saving queues it. */
  body?: string | null;
  attempts?: number | null;
  last_error?: string | null;
  external_result_id?: string | null;
  created_at?: string | null;
  queued_at?: string | null;
  leased_at?: string | null;
  sent_at?: string | null;
  updated_at?: string | null;
}

/** Whether this owner has a live Helper, and when it last checked in. Same two keys
 *  as the legacy `helper` block; `last_seen_at` is now a UTC-aware instant. */
export interface HelperPresence {
  paired: boolean;
  last_seen_at: string | null;
}

/**
 * One page of the queue.
 *
 * `counts` is over the WHOLE queue regardless of the `status` filter — the chips
 * show what selecting them would find — and every one of the six keys is present
 * even at zero, so a chip cannot vanish when it empties. `total` is the count of
 * rows matching the CURRENT filter, computed server-side; summing `counts` gives the
 * unfiltered total, which is what the "All" chip shows.
 *
 * `next_cursor` is always null: this is one of §1.3's three declared offset routes,
 * because the tab has prev/next and an "N–M of T" counter.
 */
export interface OutboxPage extends ListEnvelope<OutboxItem> {
  community: CommunityRef;
  counts: Record<OutboxStatus, number>;
  helper: HelperPresence;
}

/** The cancelled row, plus the sidebar counters the write moved. Hand `badges` to
 *  `applyBadges` (`lib/todos.svelte.ts`) and cancelling a reply costs ONE request,
 *  where today it costs a discard, a full list re-read and four badge calls. */
export interface DiscardedItem extends OutboxItem {
  badges: Badges;
}

/** One Helper installation. `skool_account` is the Skool account this install posts
 *  under, resolved to a person — null when that account is not a member of any
 *  community the caller administers, or has not been synced yet.
 *
 *  It is §2.1's `MemberRef`, so it carries `external_id` (which the legacy
 *  `SkoolAccount` did not) and `handle` is `string | null` (where the legacy declared
 *  it `string`). `postingAs()` in `lib/helper.svelte.ts` already returns a union whose
 *  other arm has a nullable handle, so widening that type is a no-op for its readers. */
export interface Device {
  id: number;
  label: string | null;
  skool_user_external_id: string | null;
  skool_account: MemberRef | null;
  last_seen_at: string | null;
  revoked_at: string | null;
  created_at: string | null;
}

/** No `community` block: devices are ACCOUNT-scoped (`/v1/devices`, not a community
 *  sub-resource). One pairing serves every community the owner holds. */
export interface DevicePage extends ListEnvelope<Device> {}

// ---------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------

export interface OutboxQuery {
  /** REPEATED on the wire (`?status=failed&status=unknown`), so the two-chip
   *  selection the page cannot express today is one request. An unrecognised value
   *  is a 400 `invalid_enum_value`, never a silent drop (§1.6). */
  status?: OutboxStatus | OutboxStatus[] | null;
  limit?: number;
  offset?: number;
}

/**
 * `GET /v1/communities/{skool_id}/outbox/items` — this owner's queue for one
 * community: what their Helper is about to send, and what has already gone out.
 *
 * Scoped to the caller, matching the lease queue: an admin sees exactly the replies
 * their own browser is responsible for, never a co-moderator's. Offset-paginated.
 */
export function fetchOutboxItems(
  skoolId: number,
  opts: OutboxQuery = {},
): Promise<OutboxPage> {
  const status =
    opts.status == null ? [] : Array.isArray(opts.status) ? opts.status : [opts.status];
  const params: QueryParams = {
    status: status.length > 0 ? status : undefined,
    limit: opts.limit,
    offset: opts.offset,
  };
  return apiGet<OutboxPage>(community(skoolId, '/outbox/items'), params);
}

/**
 * `POST /v1/communities/{skool_id}/outbox/items` — queue a reply for this owner's
 * Helper to send. Replaces `POST /admin/moderation/reply` (doc 04 §4.7: the
 * moderation reply IS an outbox row).
 *
 * Returns the QUEUED ROW, in the same representation the list returns — where the
 * legacy route answered `{status, kind, id, draft}`. The caller's `draft` is
 * `row.body`, and `row.id` is the outbox item id, which is what `discardOutboxItem`
 * needs and which the legacy response never gave you.
 *
 * UPSERT, one pending row per target per owner. Re-posting edits the pending reply
 * and re-queues it with a fresh idempotency key; it answers 200, not 201, precisely
 * because a POST that creates once and edits thereafter cannot honestly claim to
 * have created something half the time.
 *
 * THREE REFUSALS TO RENDER, not to retry through:
 *   * `409 reply_in_flight` — the existing row is leased and may already be on Skool.
 *     Wait ~2 minutes. This preserves `api/admin/moderation.py`'s guard exactly and
 *     it is the one refusal here that protects a public, irreversible side effect.
 *   * `400 reply_unaddressable` — the Skool addressing could not be resolved (the
 *     community was never synced, or the replied-to row is gone). `message` is the
 *     resolver's own sentence and is safe to show.
 *   * `422 validation_failed` — a blank body. On the legacy route a blank body
 *     DELETED the queued reply; here it is refused, and the delete is the DELETE.
 *     No caller can reach it today: the composer's Save button is disabled on empty
 *     input (`ReplyComposer.svelte:234`) and the category rename guards the same way.
 */
export function queueReply(
  skoolId: number,
  kind: 'post' | 'comment',
  id: number,
  body: string,
): Promise<OutboxItem> {
  return apiPost<OutboxItem>(community(skoolId, '/outbox/items'), {
    kind: 'comment_reply',
    target: { kind, id },
    body,
  });
}

/**
 * Queue a DIRECT MESSAGE to a member — the same route, the same queue, the same drain.
 *
 * A DM's target is a PERSON, not a place: `kind: 'member'` and the member's own id,
 * where a reply names the post or comment it hangs under. The recipient's Skool id is
 * resolved SERVER-SIDE from that member row and is deliberately not sent from here —
 * the client should not be trusted to name who a message goes to.
 *
 * NOTHING NEW BELOW THIS CALL. `enqueue_dm_draft` has parked DMs on the outbox since
 * #148 part 2, the Helper has drained them since (`DRAINABLE_KINDS` includes `dm`), and
 * `askHelperToSend()` wakes it the moment the row exists. The only thing missing was a
 * way for a human to create one, which #178 shipped: the route now takes `kind: 'dm'`
 * with a member target and resolves the recipient itself.
 *
 * QUEUED, NOT DRAFTED. The draft stage exists to get an owner's approval before words
 * go out under their name; a composer where that owner typed the words and pressed Send
 * already has it, so a second gate would be a review of yourself.
 */
export function queueDirectMessage(
  skoolId: number,
  memberId: number,
  body: string,
): Promise<OutboxItem> {
  return apiPost<OutboxItem>(community(skoolId, '/outbox/items'), {
    kind: 'dm',
    target: { kind: 'member', id: memberId },
    body,
  });
}

/**
 * Queue a LIKE for this owner's Helper to perform — the same route, the same queue and
 * the same drain as a reply.
 *
 * WHY NOT STRAIGHT TO SKOOL FROM HERE: this page has no Skool session, and there is
 * exactly one answer to that in this system rather than two. A reply written here becomes
 * an outbox row; so does a like. What makes it feel immediate is the DRAIN, not a second
 * mechanism — `askHelperToSend()` wakes a Helper in this browser the moment the row
 * exists, and if there is none the row is still queued and still goes out.
 *
 * The first version of this handed a Skool id to the local Helper over the content-script
 * bridge. It failed wherever the Helper was on another machine — while a reply from the
 * same page succeeded — held no retry, and was the one channel where the page named a
 * Skool write target. All three go away by using the queue that already existed.
 *
 * No body: a like has no content. `uq_outbox_pending_subject` includes `kind`, so this
 * neither collides with a queued reply on the same target nor duplicates on a re-click.
 */
export function queueLike(
  skoolId: number,
  kind: 'post' | 'comment',
  id: number,
): Promise<OutboxItem> {
  return apiPost<OutboxItem>(community(skoolId, '/outbox/items'), {
    kind: 'like',
    target: { kind, id },
  });
}

/**
 * `DELETE /v1/communities/{skool_id}/outbox/items/{item_id}` — cancel a queued reply
 * so the Helper will not send it. Terminal (`discarded`); the row survives as history.
 *
 * Takes the OUTBOX ITEM id, not the post/comment id — the same id the row renders
 * with, so this is a straight swap for `discardOutboxItem(skoolId, item.id)`.
 *
 * `since` is sent as the viewer's local-day start because the returned `badges` are
 * the SIDEBAR's counters and the sidebar counts today's open items (`/v1/me` is sent
 * the same instant). The queue itself is not windowed, so unlike the moderation
 * clears there is no page window that could disagree with it — which is why this is a
 * default here rather than a parameter the caller has to get right.
 *
 * `409 reply_in_flight` on a leased row (in the Helper's hands, may already have
 * landed) and on an already-terminal one (a sent reply cannot be unsent).
 */
export function discardOutboxItem(
  skoolId: number,
  itemId: number,
): Promise<DiscardedItem> {
  return apiDelete<DiscardedItem>(community(skoolId, `/outbox/items/${itemId}`), {
    since: localDayStartISO(),
  });
}

/**
 * `GET /v1/devices` — every Helper installation paired to this ACCOUNT, newest
 * first, including revoked ones.
 *
 * Account-scoped, so there is no `skoolId`: one pairing serves every community the
 * owner holds, and hanging it off one of them would be a lie about the resource. The
 * gate is `admin`, not `active` — a lapsed plan pauses sending, and must not hide
 * which browsers are holding a 90-day credential.
 *
 * Replaces `GET /outbox/devices`, which answers a BARE JSON ARRAY; this answers the
 * standard envelope, so the one-line call-site change is
 * `helper.devices = (await fetchDevices()).items`.
 *
 * The route also drops the legacy route's `@scopes(DEVICE)`: today a device token can
 * enumerate its owner's OTHER paired devices, and closing that costs nothing under a
 * per-route declaration.
 */
export function fetchDevices(opts: { limit?: number } = {}): Promise<DevicePage> {
  return apiGet<DevicePage>('/devices', { limit: opts.limit });
}
